/**
 * Third-Party Widget Fixes
 * Patches accessibility and performance issues in third-party app output
 * that cannot be fixed at the source (Judge.me, etc.)
 *
 * @version 1.0.0
 */
(function ThirdPartyFixes() {
  'use strict';

  /**
   * Judge.me Review Images & Accessibility Fix
   * Adds missing width, height, alt, and aria-label attributes to
   * Judge.me-injected images, buttons, and links to prevent layout
   * shift (CLS) and improve accessibility.
   */
  function fixJudgeMeImages() {
    // Review picture images — missing width/height
    document.querySelectorAll('.jdgm-rev__media img:not([width])').forEach(function(img) {
      if (img.naturalWidth) {
        img.setAttribute('width', img.naturalWidth);
        img.setAttribute('height', img.naturalHeight);
      } else {
        // Fallback dimensions for review thumbnails
        img.setAttribute('width', '80');
        img.setAttribute('height', '80');
      }
    });

    // Full-review picture images — missing width/height
    document.querySelectorAll('.jdgm-full-rev__pic-img:not([width])').forEach(function(img) {
      if (img.naturalWidth) {
        img.setAttribute('width', img.naturalWidth);
        img.setAttribute('height', img.naturalHeight);
      } else {
        // Fallback dimensions for full-review pictures
        img.setAttribute('width', '100');
        img.setAttribute('height', '100');
      }
    });

    // Country flag images — missing alt attributes entirely
    document.querySelectorAll('.jdgm-full-rev__location-country-flag-img').forEach(function(img) {
      if (!img.hasAttribute('alt') || img.getAttribute('alt') === 'null') {
        // The country name is rendered as adjacent text, so an unnamed flag is
        // decorative: use the title (country) when present, else alt="".
        img.setAttribute('alt', img.getAttribute('title') || '');
      }
      if (!img.hasAttribute('width')) {
        img.setAttribute('width', '16');
        img.setAttribute('height', '11');
      }
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }
    });

    // Verified-buyer badge — 18x18 inline icon, served at 2x/3x so naturalWidth
    // would over-report; layout box is always 18x18 per Judge.me's own CSS.
    // Markup varies by widget version: ".jdgm-verified-badge__image" may be the
    // <img> itself OR a wrapper around it, so size whichever <img> is present
    // (the previous selector assumed it was always the <img> and silently missed
    // the wrapper case, leaving the badge unsized → CLS).
    document.querySelectorAll('.jdgm-verified-badge__image, .jdgm-verified-badge').forEach(function(el) {
      var img = el.tagName === 'IMG' ? el : el.querySelector('img');
      if (img && !img.hasAttribute('width')) {
        img.setAttribute('width', '18');
        img.setAttribute('height', '18');
      }
    });

    // Any other Judge.me images missing alt or loading attribute. Broadened to
    // cover full-review and popup-card images that render outside .jdgm-widget.
    document.querySelectorAll('.jdgm-widget img, .jdgm-full-rev img, .jdgm-popup-card img, [class*="jdgm-"] img').forEach(function(img) {
      if (!img.hasAttribute('alt') || img.getAttribute('alt') === 'null') {
        img.setAttribute('alt', '');
      }
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }
    });

    // Popup card review buttons — no text content, inaccessible
    document.querySelectorAll('.jdgm-popup-card__full-rev-link').forEach(function(btn) {
      if (!btn.textContent.trim() && !btn.hasAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'View full review');
      }
    });

    // Empty links in Judge.me widgets — no accessible name
    document.querySelectorAll('.jdgm-widget a').forEach(function(link) {
      if (!link.textContent.trim() && !link.hasAttribute('aria-label')) {
        var title = link.getAttribute('title') || '';
        link.setAttribute('aria-label', title || 'Review link');
      }
    });

    // Popup card review images — add dimensions for layout stability
    document.querySelectorAll('.jdgm-popup-card img:not([width])').forEach(function(img) {
      if (img.naturalWidth) {
        img.setAttribute('width', img.naturalWidth);
        img.setAttribute('height', img.naturalHeight);
      } else {
        img.setAttribute('width', '240');
        img.setAttribute('height', '240');
      }
      img.style.aspectRatio = '1/1';
    });
  }

  /**
   * Ease Product Attachments (Tigren) Fix
   * The app injects, per attachment, an icon-only <a><img></a> plus a
   * text link (.tigren-linkAttachment) to the same href. The icon <img>
   * ships without width/height (CLS), and the icon link's only name is
   * the img alt — empty when the attachment has no title.
   */
  function fixAttachmentWidgets() {
    // Icon images — rendered at max-height 40px by the app's own CSS
    document.querySelectorAll('.tigren-iconAttachment:not([width])').forEach(function(img) {
      img.setAttribute('width', '40');
      img.setAttribute('height', '40');
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }
    });

    // Icon-only links: when a named text link to the same href sits in the
    // same attachment item, the icon link is a duplicate — remove it from
    // the a11y tree. Otherwise make sure it has an accessible name.
    document.querySelectorAll('.tigren-product-attachments a, .tigren-attachments a, .tigren-pageAttachments a').forEach(function(link) {
      if (link.classList.contains('tigren-linkAttachment')) return;
      if (link.getAttribute('aria-hidden') === 'true' || link.hasAttribute('aria-label')) return;

      var item = link.closest('.tigren-productAttachment, .tigren-pageAttachment') || link.parentElement;
      var titleLink = item && item.querySelector('.tigren-linkAttachment');
      var img = link.querySelector('img');
      var imgAlt = img && img.getAttribute('alt');

      if (titleLink && titleLink.getAttribute('href') === link.getAttribute('href') && titleLink.textContent.trim()) {
        link.setAttribute('aria-hidden', 'true');
        link.setAttribute('tabindex', '-1');
      } else if (!link.textContent.trim() && !imgAlt) {
        var href = link.getAttribute('href') || '';
        var file = decodeURIComponent(href.split('/').pop() || '').split('?')[0];
        link.setAttribute('aria-label', file ? 'Download ' + file : 'Download attachment');
      }
    });
  }

  function runFixes() {
    fixJudgeMeImages();
    fixAttachmentWidgets();
  }

  // Run as early as possible, then again on a staggered schedule, so attributes
  // land before accessibility audits / screen readers reach the widget — instead
  // of waiting a flat 2s during which the un-patched markup is exposed. The
  // MutationObserver below still catches any late or re-rendered widgets.
  function scheduleFixes() {
    runFixes();
    [300, 800, 1500, 3000].forEach(function(delay) {
      setTimeout(runFixes, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleFixes);
  } else {
    scheduleFixes();
  }

  // Observe for dynamically injected Judge.me / attachment-widget content
  var observer = new MutationObserver(function(mutations) {
    var hasWidget = mutations.some(function(m) {
      return m.addedNodes.length && Array.from(m.addedNodes).some(function(n) {
        if (n.nodeType !== 1) return false;
        if (n.classList && (n.classList.contains('jdgm-widget') || n.classList.contains('tigren-productAttachment') || n.classList.contains('tigren-attachments'))) return true;
        return !!(n.querySelector && n.querySelector('.jdgm-widget, .tigren-attachments, .tigren-productAttachment'));
      });
    });
    if (hasWidget) {
      setTimeout(runFixes, 500);
    }
  });

  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true
  });
})();
